import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  CardContent,
  Grid,
  Typography,
  Chip,
  IconButton,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useSelector } from 'react-redux';
import { getProductsMarketPlace } from '../../api/marcketPlace';
import Loading from '../../components/Loading';
import { HomeLayouts } from '../../components/layouts/HomeLayouts';
import { PublicMarketplaceLayout } from '../../components/layouts/PublicMarketplaceLayout';
import notImage from '../../assets/product-default.png';
import imgr from '../../assets/fill.png';
import PopUp from '../../components/PopUp';
import QuoteFlash from '../../components/PopUp/components/Flash/QuoteFlash';
import styles from './productDetail.module.scss';

const categoryEmojis = {
  'Frutas y Verduras': '🥬',
  Carnicos: '🥩',
  Abarrote: '🍞',
  Abarrotes: '🍞',
  Bebidas: '🥤',
  'Material operativo': '🧂',
  'Pescados y Mariscos': '🐟',
};

export const ProductDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { token, user } = useSelector((state) => state.auth);
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);

  const isPublicAccess = !user || !token;

  useEffect(() => {
    loadProduct();
  }, [id]);

  const loadProduct = async () => {
    setLoading(true);
    try {
      const response = await getProductsMarketPlace(token || '', '1000', '', '');
      if (response.ok) {
        const foundProduct = response.data.find((p) => p.id === parseInt(id));
        if (foundProduct) {
          setProduct({ ...foundProduct, cantidad: 1 });
        }
      }
    } catch (error) {
      console.error('Error loading product:', error);
    }
    setLoading(false);
  };

  const handleBack = () => {
    navigate(-1);
  };

  const handleQuote = () => {
    setShowModal(true);
  };

  if (loading) {
    const LayoutComponent = isPublicAccess ? PublicMarketplaceLayout : HomeLayouts;
    return (
      <LayoutComponent>
        <Box
          sx={{
            minHeight: '80vh',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Loading />
        </Box>
      </LayoutComponent>
    );
  }

  if (!product) {
    const LayoutComponent = isPublicAccess ? PublicMarketplaceLayout : HomeLayouts;
    return (
      <LayoutComponent>
        <Box sx={{ padding: 4, textAlign: 'center' }}>
          <Typography variant="h5" color="text.secondary">
            Producto no encontrado
          </Typography>
          <Button onClick={handleBack} sx={{ marginTop: 2 }}>
            Volver
          </Button>
        </Box>
      </LayoutComponent>
    );
  }

  const LayoutComponent = isPublicAccess ? PublicMarketplaceLayout : HomeLayouts;
  const emoji = categoryEmojis[product.category] || '📦';

  return (
    <LayoutComponent>
      <PopUp size={'large'} setShowModal={setShowModal} showModal={showModal}>
        <QuoteFlash item={product} setShowModal={setShowModal} />
      </PopUp>

      <Box sx={{ paddingY: 4, paddingX: { xs: 2, md: 8 }, minHeight: '80vh' }}>
        {/* Back Button */}
        <Box sx={{ marginBottom: 3 }}>
          <IconButton onClick={handleBack} sx={{ color: '#32344B' }}>
            <ArrowBackIcon />
          </IconButton>
        </Box>

        <Grid container spacing={4}>
          {/* Product Image */}
          <Grid item xs={12} md={6}>
            <Card
              sx={{
                borderRadius: 4,
                overflow: 'hidden',
                boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
                backgroundColor: '#f8f9fa',
              }}
            >
              <Box
                component="img"
                src={product.image || notImage}
                alt={product.name}
                sx={{
                  width: '100%',
                  height: { xs: '320px', md: '450px' },
                  objectFit: 'contain',
                }}
              />
            </Card>
          </Grid>

          {/* Product Info */}
          <Grid item xs={12} md={6}>
            <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
              {/* Category Badge */}
              <Chip
                label={`${emoji} ${product.category}`}
                sx={{
                  backgroundColor: '#32344B',
                  color: '#fff',
                  fontWeight: 'bold',
                  marginBottom: 2,
                  width: 'fit-content',
                  fontSize: '14px',
                }}
              />

              {/* Product Name */}
              <Typography
                variant="h3"
                sx={{
                  fontWeight: 'bold',
                  color: '#333',
                  marginBottom: 2,
                  fontSize: { xs: '1.75rem', md: '2.5rem' },
                }}
              >
                {product.name}
              </Typography>

              {/* Supplier Info */}
              <Box sx={{ marginBottom: 3 }}>
                <Typography variant="body1" sx={{ color: '#666', marginBottom: 1 }}>
                  <strong>Proveedor:</strong>
                </Typography>
                <Typography variant="h6" sx={{ color: '#72BFBA', fontWeight: 'bold' }}>
                  {product.supplier}
                </Typography>
              </Box>

              {/* Price (if available) */}
              {product.price && (
                <Box sx={{ marginBottom: 3 }}>
                  <Typography variant="body1" sx={{ color: '#666', marginBottom: 1 }}>
                    <strong>Precio referencial:</strong>
                  </Typography>
                  <Typography variant="h4" sx={{ color: '#32344B', fontWeight: 'bold' }}>
                    ${product.price}
                    <Typography
                      component="span"
                      variant="body1"
                      sx={{ color: '#999', marginLeft: 1 }}
                    >
                      por {product.measurementUnit || 'unidad'}
                    </Typography>
                  </Typography>
                </Box>
              )}

              {/* Product Description */}
              {product.description && (
                <Box
                  sx={{
                    backgroundColor: '#f8f9fa',
                    padding: 2.5,
                    borderRadius: 2,
                    marginBottom: 3,
                    border: '1px solid #e9ecef',
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', marginBottom: 1 }}>
                    <Box
                      sx={{
                        width: 4,
                        height: 20,
                        backgroundColor: '#32344B',
                        borderRadius: 1,
                        marginRight: 1.5,
                      }}
                    />
                    <Typography
                      variant="subtitle1"
                      sx={{
                        fontWeight: 'bold',
                        color: '#32344B',
                        fontSize: '16px',
                      }}
                    >
                      Descripción del producto
                    </Typography>
                  </Box>
                  <Typography
                    variant="body1"
                    sx={{
                      color: '#555',
                      lineHeight: 1.8,
                      fontSize: '15px',
                      paddingLeft: 2.5,
                    }}
                  >
                    {product.description}
                  </Typography>
                </Box>
              )}

              {/* Info Box */}
              <Box
                sx={{
                  backgroundColor: '#f8f9fa',
                  padding: 3,
                  borderRadius: 3,
                  marginBottom: 3,
                  marginTop: 'auto',
                }}
              >
                <Typography variant="body2" sx={{ color: '#666', marginBottom: 1 }}>
                  💡 <strong>¿Cómo funciona?</strong>
                </Typography>
                <Typography variant="body2" sx={{ color: '#666' }}>
                  Solicita una cotización y el proveedor te responderá con precios y
                  disponibilidad personalizados para tu empresa.
                </Typography>
              </Box>

              {/* Quote Button */}
              <Button
                onClick={handleQuote}
                fullWidth
                variant="contained"
                size="large"
                sx={{
                  backgroundColor: '#32344B',
                  color: '#fff',
                  borderRadius: 10,
                  height: 56,
                  fontSize: '16px',
                  fontWeight: 'bold',
                  '&:hover': {
                    backgroundColor: '#57575b',
                  },
                  display: 'flex',
                  gap: 1,
                }}
              >
                <img src={imgr} alt="" style={{ width: '20px', height: '20px' }} />
                Cotizar ahora
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Box>
    </LayoutComponent>
  );
};
